import React, { useEffect, useRef, useState } from "react";

interface AudioBarsProps {
  /** Function returning a Uint8Array of frequency data, or null. */
  getFrequencyData: () => Uint8Array | null | undefined;
  bars?: number;
  color?: string; // tailwind bg class
  className?: string;
  heightPx?: number;
}

/**
 * Animated audio waveform bars driven by real-time frequency data.
 * Reads via rAF; falls back to subtle idle animation when no data.
 */
const AudioBars: React.FC<AudioBarsProps> = ({
  getFrequencyData,
  bars = 5,
  color = "bg-white",
  className = "",
  heightPx = 16,
}) => {
  const [levels, setLevels] = useState<number[]>(() => Array(bars).fill(0.2));
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    let mounted = true;
    const tick = () => {
      const data = getFrequencyData();
      if (data && data.length > 0) {
        const step = Math.floor(data.length / bars) || 1;
        const next: number[] = [];
        for (let i = 0; i < bars; i++) {
          let sum = 0;
          for (let j = 0; j < step; j++) sum += data[i * step + j] || 0;
          next.push(Math.min(1, sum / step / 200));
        }
        if (mounted) setLevels(next);
      } else {
        // Idle pulse fallback
        const t = Date.now() / 200;
        if (mounted)
          setLevels(Array.from({ length: bars }, (_, i) => 0.25 + 0.15 * Math.abs(Math.sin(t + i))));
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      mounted = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [getFrequencyData, bars]);

  return (
    <div
      className={`flex items-center justify-center gap-[2px] ${className}`}
      style={{ height: heightPx }}
      aria-hidden
    >
      {levels.map((lvl, i) => (
        <span
          key={i}
          className={`${color} rounded-full`}
          style={{
            width: 2,
            height: `${Math.max(15, lvl * 100)}%`,
            transition: "height 80ms linear",
          }}
        />
      ))}
    </div>
  );
};

export default AudioBars;
